// src/app/[lang]/shop/carpets/[titleAtURL]/ShareButton.jsx

'use client'


import { useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Button from '@mui/material/Button';
import IosShareIcon from '@mui/icons-material/IosShare';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';


export default function ShareButton({ dict }) {

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('success');

  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Extract share-related translations from dict or use defaults
  const translations = {
    share: dict?.share || 'Share',
    shareSuccess: dict?.shareSuccess || 'Successfully shared!',
    shareError: dict?.shareError || 'Failed to share',
    copySuccess: dict?.copySuccess || 'Link copied to clipboard!',
    copyError: dict?.copyError || 'Failed to copy link'
  };

  const handleShare = async () => {
    // Get the full URL including search params
    const url = window.location.origin + pathname + 
      (searchParams.toString() ? `?${searchParams.toString()}` : '');

    // Check if the Web Share API is supported
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          url: url
        });
        setMessage(translations.shareSuccess);
        setSeverity('success');
        setOpen(true);
      } catch (error) {
        // User cancelled or share failed
        if (error.name !== 'AbortError') {
          setMessage(translations.shareError);
          setSeverity('error');
          setOpen(true);
        }
      }
    } else {
      // Fallback for browsers that don't support the Web Share API
      try {
        await navigator.clipboard.writeText(url);
        setMessage(translations.copySuccess);
        setSeverity('success');
        setOpen(true);
      } catch (error) {
        setMessage(translations.copyError);
        setSeverity('error');
        setOpen(true);
      }
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <Button 
        variant="outlined" 
        onClick={handleShare}
        aria-label={translations.share}
        sx={{ 
          height: '100%',
          width: '2.5rem',
          minWidth: '2.5rem',
          padding: '.375rem 0',
          borderRadius: '.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <IosShareIcon sx={{ color: 'var(--clr-text)' }} />
      </Button>

      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );
}
